import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '').trim().toLowerCase();
  const base = { label: statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—' };
  if (s === 'aprobada' || s === 'convertida a oc') {
    return { ...base, bg: isDarkMode ? '#14532d' : '#dcfce7', color: isDarkMode ? '#bbf7d0' : '#166534', border: isDarkMode ? '#22c55e' : '#86efac' };
  }
  if (s === 'enviada') {
    return { ...base, bg: isDarkMode ? '#1e3a5f' : '#dbeafe', color: isDarkMode ? '#93c5fd' : '#1d4ed8', border: isDarkMode ? '#3b82f6' : '#93c5fd' };
  }
  if (s === 'rechazada') {
    return { ...base, bg: isDarkMode ? '#7f1d1d' : '#fee2e2', color: isDarkMode ? '#fca5a5' : '#b91c1c', border: isDarkMode ? '#ef4444' : '#fecaca' };
  }
  return { ...base, bg: isDarkMode ? '#27272a' : '#f0ebe3', color: isDarkMode ? '#e4e4e7' : '#44403c', border: isDarkMode ? '#3f3f46' : '#d6d0c4' };
};

const motiveLabel = (row) => {
  const motive = String(row.purchase_motive || '').trim().toLowerCase();
  if (motive === 'sale') {
    const sale = String(row.sale_code || '').trim();
    return sale ? `Venta · ${sale}` : 'Venta';
  }
  if (motive === 'stock') return 'Stock';
  return row.purchase_motive || '—';
};

const TABLE_COLUMNS = [
  { key: 'Solicitud', align: 'left', sortKey: 'request_code' },
  { key: 'Código', align: 'left', sortKey: 'item_code' },
  { key: 'Descripción', align: 'left', sortKey: 'description' },
  { key: 'Motivo', align: 'left', sortKey: 'purchase_motive' },
  { key: 'Comprador', align: 'left', sortKey: 'buyer_name' },
  { key: 'Cantidad', align: 'right', sortKey: 'quantity' },
  { key: 'P. unit.', align: 'right', sortKey: 'unit_budget' },
  { key: 'Total', align: 'right', sortKey: 'line_total' },
  { key: 'Enlace', align: 'left', sortKey: 'reference_link' },
  { key: 'Estado', align: 'center', sortKey: 'request_status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'request_code':
      return String(row.request_code || '').toLowerCase();
    case 'item_code':
      return String(row.item_code || '').toLowerCase();
    case 'description':
      return String(row.description || '').toLowerCase();
    case 'purchase_motive':
      return motiveLabel(row).toLowerCase();
    case 'buyer_name':
      return String(row.buyer_name || '').toLowerCase();
    case 'quantity':
      return Number(row.quantity || 0);
    case 'unit_budget':
      return Number(row.unit_budget || row.estimated_unit_price || 0);
    case 'reference_link':
      return String(row.reference_link || '').toLowerCase();
    case 'estimated_unit_price':
      return Number(row.estimated_unit_price || 0);
    case 'line_total':
      return Number(row.line_total || 0);
    case 'request_status':
      return String(row.request_status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const UpsellingRequestItemsTable = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  hideChrome = false,
  onRequestClick,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('request_code');
  const [sortDir, setSortDir] = useState('desc');

  const statusOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.request_status || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.request_code,
          row.requester_name,
          row.item_code,
          row.description,
          row.buyer_name,
          row.purchase_motive,
          row.sale_code,
          row.reference_link,
          row.supplier_name,
          row.request_status,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) list = list.filter((row) => String(row.request_status || '').trim() === filterStatus);
    return list;
  }, [rows, searchQuery, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const handleSortClick = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(['quantity', 'unit_budget', 'estimated_unit_price', 'line_total'].includes(key) ? 'desc' : 'asc');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || filterStatus);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} ítems`
        : `${rows.length} ítems`);

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    minWidth: 140,
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  return (
    <div style={hideChrome ? { background: bg } : { border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden', background: bg }}>
      {hideChrome ? null : (
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>Ítems upselling</h3>
          <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
        </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Solicitud, código, descripción…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Estado</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button type="button" onClick={clearFilters} style={{ ...selectStyle, cursor: 'pointer' }}>
            Limpiar
          </button>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1280 }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando ítems…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay ítems para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const st = statusPresentation(row.request_status, isDarkMode);
                return (
                  <tr
                    key={row.id}
                    style={{ borderBottom: `1px solid ${border}`, background: bg }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = bg;
                    }}
                  >
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <button
                        type="button"
                        onClick={() =>
                          onRequestClick?.({
                            id: row.purchase_request_id,
                            request_code: row.request_code,
                          })
                        }
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 12, color: ui.link || '#2563eb' }}>
                          {row.request_code || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: muted }}>{row.requester_name || '—'}</div>
                      </button>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text, fontWeight: 600 }}>
                      {row.item_code || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>{row.description || '—'}</td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>{motiveLabel(row)}</td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>{row.buyer_name || '—'}</td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, textAlign: 'right', color: text }}>
                      {Number(row.quantity || 0).toLocaleString('es-CL')} {row.unit || ''}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, textAlign: 'right', color: muted }}>
                      {formatMoney(row.unit_budget || row.estimated_unit_price)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, textAlign: 'right', fontWeight: 700, color: text }}>
                      {formatMoney(row.line_total)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text, maxWidth: 180 }}>
                      {row.reference_link ? (
                        <a
                          href={row.reference_link}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: ui.link || '#2563eb', wordBreak: 'break-all' }}
                        >
                          Enlace
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          background: st.bg,
                          color: st.color,
                          border: `1px solid ${st.border}`,
                        }}
                      >
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UpsellingRequestItemsTable;
